import * as JobDetailActions from 'src/admin-app/jobs/job-detail/+state/job-detail.actions';
import * as QuoteDetailActions from 'src/admin-app/quotes/quote-detail/+state/quote-detail.actions';
import * as RailroadDetailActions from './railroad-detail.actions';
import * as RailroadDetailSelectors from './railroad-detail.selectors';

import { Action, Store, select } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { CustomerClient, JobsClient, PersonnelClient } from 'src/shared/api';
import { SERVICE_TYPE, getAllListArgs } from 'src/shared/constants';
import {
	concatMap,
	delay,
	filter,
	map,
	mapTo,
	switchMap,
	tap,
	withLatestFrom,
} from 'rxjs/operators';

import { AlertDialogComponent } from 'src/shared/alerts/alert-dialog/alert-dialog.component';
import { DataPersistence } from '@nrwl/angular';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RailroadDetailComponent } from '../railroad-detail.component';
import { RailroadDetailPartialState } from './railroad-detail.reducer';
import { grabIdFromParams } from 'src/shared/utilities/utility-functions';
import { notifyAfterSaving } from 'src/shared/utilities/custom-rxjs-operators';
import { railroadFormToRailroad } from '../railroad-detail.utility';
import { railroadsFragments } from '../../railroads.routes';

@Injectable()
export class RailroadDetailEffects {
	selectRailroad$ = createEffect(() =>
		this.dataPersistence.navigation(RailroadDetailComponent, {
			run: (
				r: ActivatedRouteSnapshot,
				_state: RailroadDetailPartialState
			) => {
				const railroadId = grabIdFromParams(r.paramMap);

				return RailroadDetailActions.railroadSelected({ railroadId });
			},
			onError: (_a: ActivatedRouteSnapshot, error: any) => {
				throw new Error(error);
			},
		})
	);

	loadRailroadFromStore$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RailroadDetailActions.railroadSelected),
			map((action) => action.railroadId),
			filter((id) => id > 0),
			withLatestFrom(
				this.store.pipe(
					select(RailroadDetailSelectors.getSelectedRailroad)
				),
				(_id, railroad) => railroad
			),
			filter(Boolean),
			map((railroad) =>
				RailroadDetailActions.railroadLoadedFromStore({ railroad })
			)
		)
	);

	loadRailroadFromApi$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				...[
					RailroadDetailActions.railroadSelected,
					QuoteDetailActions.railroadSelected,
					QuoteDetailActions.railroadRequested,
					JobDetailActions.railroadSelected,
					JobDetailActions.railroadRequested,
					JobDetailActions.railroadRequestedFromAssignments,
				]
			),
			map(
				(action: Partial<Action> & { railroadId?: number }) =>
					action.railroadId
			),
			filter((id) => id > 0),
			withLatestFrom(
				this.store.pipe(
					select(RailroadDetailSelectors.getAllRailroadEntities)
				)
			),
			filter(([id, railroads]) => !railroads[id]),
			concatMap(([id, _railroads]) =>
				this.customerClient.getRailroad(id).pipe(
					map((railroad) =>
						RailroadDetailActions.railroadLoadedFromApi({
							railroad,
						})
					)
				)
			)
		)
	);

	startNewRailroad$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RailroadDetailActions.railroadSelected),
			map((action) => action.railroadId),
			filter((id) => id === 0),
			mapTo(RailroadDetailActions.newRailroadStarted())
		)
	);

	clearFormError$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RailroadDetailActions.saveRequestedWithInvalidForm),
			delay(1000),
			mapTo(RailroadDetailActions.formErrorCleared())
		)
	);

	saveRailroad$ = createEffect(() =>
		this.dataPersistence.pessimisticUpdate(
			RailroadDetailActions.saveRequested,
			{
				run: (
					action: ReturnType<
						typeof RailroadDetailActions.saveRequested
					>,
					_state: RailroadDetailPartialState
				) => {
					const railroad = railroadFormToRailroad(action.formValue);

					if (railroad.isIntegratedWithFlairDocs) {
						if (
							railroad.contractedRates &&
							!railroad.contractedRates.find(
								(rate) =>
									rate.serviceType.id ===
									SERVICE_TYPE.Monitoring
							)
						) {
							return RailroadDetailActions.userReportableErrorSavingRailroad(
								{
									message:
										'Missing a Contracted Rates for the Monitoring Service.',
								}
							);
						}
						if (
							railroad.offeredServices &&
							!railroad.offeredServices.find(
								(s) => s.type.id === SERVICE_TYPE.Monitoring
							)
						) {
							return RailroadDetailActions.userReportableErrorSavingRailroad(
								{
									message:
										'Missing an Offered Service with a service type of Monitoring .',
								}
							);
						}
					}

					return this.customerClient.saveRailroad(railroad).pipe(
						map((id) =>
							railroad.id === 0
								? RailroadDetailActions.newRailroadSaved({
										railroad: { ...railroad, id },
								  })
								: RailroadDetailActions.railroadSaved({
										railroad,
								  })
						)
					);
				},
				onError: (
					_action: ReturnType<
						typeof RailroadDetailActions.saveRequested
					>,
					error: any
				) => {
					return RailroadDetailActions.errorSavingRailroad({ error });
				},
			}
		)
	);

	throwIfError$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					RailroadDetailActions.errorSavingRailroad,
					RailroadDetailActions.errorLoadingRailroad
				),
				map(({ error }) => {
					throw Error(error);
				})
			),
		{ dispatch: false }
	);

	closeAfterSaving$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(RailroadDetailActions.railroadSaved),
				withLatestFrom(
					this.store.pipe(
						select(RailroadDetailSelectors.getCloseAfterSaving)
					),
					(_, shouldClose) => shouldClose
				),
				filter(Boolean),
				tap(() => this.router.navigate(railroadsFragments.list))
			),
		{ dispatch: false }
	);

	// TODO: Replace with SignalR
	// Purpose: fill in primary keys of potentially-added child objects
	refreshRailroadAfterSaving$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RailroadDetailActions.railroadSaved),
			concatMap(({ railroad }) =>
				this.customerClient.getRailroad(railroad.id)
			),
			map((railroad) =>
				RailroadDetailActions.railroadLoadedFromApi({
					railroad,
				})
			)
		)
	);

	navigateToCreatedRailroad$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(RailroadDetailActions.newRailroadSaved),
				map((action) => action.railroad.id),
				tap((id) => this.router.navigate(railroadsFragments.detail(id)))
			),
		{ dispatch: false }
	);

	notifyAfterSaving$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(RailroadDetailActions.railroadSaved),
				notifyAfterSaving(this.snackbar)
			),
		{ dispatch: false }
	);

	popUpErrorMessage$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(RailroadDetailActions.userReportableErrorSavingRailroad),
				tap(({ message }) =>
					this.dialog.open(AlertDialogComponent, {
						data: { message, showFailureButton: false },
					})
				)
			),
		{ dispatch: false }
	);

	loadCustomers$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RailroadDetailActions.railroadSelected),
			map((action) => action.railroadId),
			filter((id) => id > 0),
			switchMap((id) =>
				this.customerClient
					.getCustomers({ ...getAllListArgs, railroadId: id })
					.pipe(
						map((pagedResults) => pagedResults.results),
						map((customers) =>
							RailroadDetailActions.customerssLoaded({
								customers,
							})
						)
					)
			)
		)
	);

	loadRwics$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RailroadDetailActions.railroadSelected),
			map((action) => action.railroadId),
			filter((id) => id > 0),
			switchMap((id) =>
				this.personnelClient
					.getFieldWorkers({ ...getAllListArgs, customerId: id })
					.pipe(
						map((pagedResults) => pagedResults.results),
						map((rwics) =>
							RailroadDetailActions.rwicsLoaded({
								rwics,
							})
						)
					)
			)
		)
	);

	loadJobs$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RailroadDetailActions.railroadSelected),
			map((action) => action.railroadId),
			filter((id) => id > 0),
			switchMap((id) =>
				this.jobsClient
					.getJobs({ ...getAllListArgs, customerId: id })
					.pipe(
						map((pagedResults) => pagedResults.results),
						map((jobs) =>
							RailroadDetailActions.jobsLoaded({
								jobs,
							})
						)
					)
			)
		)
	);

	constructor(
		private readonly actions$: Actions,
		private readonly store: Store<RailroadDetailPartialState>,
		private readonly dataPersistence: DataPersistence<
			RailroadDetailPartialState
		>,
		private readonly customerClient: CustomerClient,
		private readonly personnelClient: PersonnelClient,
		private readonly jobsClient: JobsClient,
		private readonly router: Router,
		private readonly snackbar: MatSnackBar,
		private readonly dialog: MatDialog
	) {}
}
